import { useUiStrings } from "@seagames/ui";
import { useQuery } from "@tanstack/react-query";
import { Link, Outlet, createFileRoute, redirect, useRouterState } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { usePageBar } from "../../components/shell/page-bar";
import { mealPlanOptions } from "../../lib/api/fnb";
import { useDefaultGamesId } from "../../lib/competition/games";
import { canSeeFnb, canSeeHostOrders, fnbView } from "../../lib/fnb/access";

/**
 * Food & Beverages (M12, PLAN §4 / F13) — the layout: her tab row over the page.
 *   host    Overview · Dining halls · Takeaway orders (badge: orders to confirm)
 *   scan    Scan meal pass (the door station) · Takeaway orders when the group also orders
 *   orders  Takeaway orders only (a host group that only orders), no tabs
 *   NOC     one page (where the delegation eats + its packed meals), no tabs
 *
 * Who: `canSeeFnb` (admins, a group with any `fnb.*`). A courtesy, not the
 * control — the API scopes every row in SQL and checks every write.
 */
export const Route = createFileRoute("/_app/logistics/food-beverages")({
	validateSearch: z.object({ games_id: z.string().uuid().optional().catch(undefined) }),
	beforeLoad: ({ context }) => {
		if (!canSeeFnb(context.session)) throw redirect({ to: "/" });
	},
	staticData: { contentTop: "tabbed" },
	component: FnbLayout,
});

type TabTo = "/logistics/food-beverages" | "/logistics/food-beverages/halls" | "/logistics/food-beverages/orders";
type Tab = { key: "overview" | "scan" | "halls" | "orders"; to: TabTo; badge?: number; badgeHint?: string };

function FnbLayout() {
	const { t } = useTranslation("fnb");
	const { t: tn } = useTranslation("nav");
	const { formatNumber: fmt } = useUiStrings();
	const { session } = Route.useRouteContext();
	const search = Route.useSearch();
	const pathname = useRouterState({ select: (s) => s.location.pathname });
	const view = fnbView(session);
	const gamesId = useDefaultGamesId(search.games_id);
	// The Takeaway orders tab's red count: orders to confirm — the Overview's own query on today.
	const plan = useQuery(mealPlanOptions(gamesId ?? "", undefined, !!gamesId && view === "host"));
	const pending = plan.data?.attention.pending_orders;

	// Her heading in either menu: "Food & Beverages / Logistics & Hospitality".
	usePageBar({ subtitle: tn("sections.logistics") });

	const tabs: Tab[] = [];
	if (view === "host") {
		tabs.push({ key: "overview", to: "/logistics/food-beverages" }, { key: "halls", to: "/logistics/food-beverages/halls" });
		tabs.push({
			key: "orders",
			to: "/logistics/food-beverages/orders",
			badge: pending,
			badgeHint: pending !== undefined ? t("tabs.toConfirm", { count: pending, n: fmt(pending) }) : undefined,
		});
	} else if (view === "scan") {
		tabs.push({ key: "scan", to: "/logistics/food-beverages" });
		if (canSeeHostOrders(session)) tabs.push({ key: "orders", to: "/logistics/food-beverages/orders" });
	}
	const current = (to: TabTo) => (to === "/logistics/food-beverages" ? pathname === to || pathname === `${to}/` : pathname.startsWith(to));

	return (
		<div className="flex min-w-0 flex-col gap-[1.125rem]">
			{tabs.length > 1 && (
				<nav aria-label={t("tabs.label")} className="-mt-[0.25rem] flex gap-[0.25rem] overflow-x-auto border-b border-gms-line [scrollbar-width:none]">
					{tabs.map((tab) => (
						<Link
							key={tab.key}
							to={tab.to}
							search={(p: { games_id?: string }) => ({ games_id: p.games_id })}
							aria-current={current(tab.to) ? "page" : undefined}
							className={`relative inline-flex h-[2.75rem] shrink-0 items-center gap-[0.375rem] px-[0.75rem] text-[0.875rem] whitespace-nowrap transition-colors outline-none focus-visible:ring-2 focus-visible:ring-gms-navy/40 ${
								current(tab.to)
									? "font-semibold text-gms-navy after:absolute after:inset-x-[0.5rem] after:-bottom-px after:h-[3px] after:rounded-t-[3px] after:bg-gms-red"
									: "font-medium text-gms-ink-2 hover:text-gms-navy"
							}`}
						>
							{t(`tabs.${tab.key}`)}
							{!!tab.badge && tab.badge > 0 && (
								<span
									title={tab.badgeHint}
									className="inline-grid h-[1.125rem] min-w-[1.25rem] place-items-center rounded-full bg-gms-red px-[0.3125rem] text-[0.6875rem] font-bold text-white tabular-nums"
								>
									{fmt(tab.badge)}
								</span>
							)}
						</Link>
					))}
				</nav>
			)}
			<Outlet />
		</div>
	);
}
